import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Film, Search } from "lucide-react";
import { getDramas } from "@/services/firebase/drama";
import {
  listAdUnlockStats,
  currentPeriodTaipei,
} from "@/services/firebase/ad_unlock_stats";
import DramaCard from "@/components/drama/DramaCard";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { AdminSearchInput } from "@/components/admin-ui/admin-search-input";
import { AdminLoadingSkeleton } from "@/components/admin-ui/admin-loading-skeleton";
import { AdminEmptyState } from "@/components/admin-ui/admin-empty-state";
import { AdminErrorAlert } from "@/components/admin-ui/admin-error-alert";
import { Button } from "@/components/ui/button";

type SortMode = "default" | "ad_unlock_desc";

export default function DramaListPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterReleased, setFilterReleased] = useState<boolean | null>(null);
  const [filterNewRelease, setFilterNewRelease] = useState<boolean | null>(null);
  const [filterLandscape, setFilterLandscape] = useState<boolean | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>("default");

  const { data: dramas, isLoading, error, refetch } = useQuery({
    queryKey: ["dramas"],
    queryFn: () => getDramas(),
  });

  // 本月廣告解鎖次數（批查，避免 N+1）。drama 列表載完才打。
  const period = currentPeriodTaipei();
  const enabledDramaIds = useMemo(
    () =>
      (dramas ?? [])
        .filter((d) => d.ad_unlock_enabled === true)
        .map((d) => d.id || d.drama_id)
        .filter((id): id is string => !!id),
    [dramas]
  );
  const { data: adUnlockTotals } = useQuery({
    queryKey: ["ad-unlock-totals", period, enabledDramaIds],
    queryFn: () => listAdUnlockStats(enabledDramaIds, period),
    enabled: enabledDramaIds.length > 0,
  });
  const totalsMap = adUnlockTotals?.totals ?? {};

  const filteredDramas = useMemo(() => {
    if (!dramas || dramas.length === 0) return [];

    const searchLower = searchTerm.toLowerCase();
    const filtered = dramas.filter((drama) => {
      const title = drama.drama_title?.toLowerCase() || "";
      const titleEn = drama.drama_title_en?.toLowerCase() || "";
      const matchesSearch =
        !searchLower || title.includes(searchLower) || titleEn.includes(searchLower);
      const matchesReleased =
        filterReleased === null || drama.isReleased === filterReleased;
      const matchesNewRelease =
        filterNewRelease === null || drama.new_release === filterNewRelease;
      const matchesLandscape =
        filterLandscape === null ||
        (drama.drama_video_orientation === 1) === filterLandscape;
      return matchesSearch && matchesReleased && matchesNewRelease && matchesLandscape;
    });

    if (sortMode === "ad_unlock_desc") {
      // 廣告解鎖排序：有開啟 + 本月次數高 → 前面；未開啟 → 後面
      return [...filtered].sort((a, b) => {
        const aId = a.id || a.drama_id || "";
        const bId = b.id || b.drama_id || "";
        const aCount = totalsMap[aId] ?? -1;
        const bCount = totalsMap[bId] ?? -1;
        return bCount - aCount;
      });
    }
    return filtered;
  }, [dramas, searchTerm, filterReleased, filterNewRelease, sortMode, totalsMap]);

  const handleCardClick = (dramaId: string) => {
    navigate(`/dramas/${dramaId}`);
  };

  if (isLoading) {
    return (
      <AdminPage>
        <AdminPageHeader title="劇集管理" />
        <AdminLoadingSkeleton variant="card" rows={10} />
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage>
        <AdminPageHeader title="劇集管理" />
        <AdminErrorAlert
          message={`載入劇集時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title="劇集管理"
        subtitle={`顯示 ${filteredDramas.length} / ${dramas?.length || 0} 個劇集`}
        createHref="/dramas/new"
        createLabel="新增劇集"
        onRefresh={() => refetch()}
        loading={isLoading}
        actions={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <AdminSearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="搜尋劇集標題..."
              className="max-w-xs"
            />
            <div className="flex gap-2">
              <Button
                variant={filterReleased === true ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterReleased(filterReleased === true ? null : true)}
              >
                已上架
              </Button>
              <Button
                variant={filterReleased === false ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterReleased(filterReleased === false ? null : false)}
              >
                未上架
              </Button>
              <Button
                variant={filterNewRelease === true ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterNewRelease(filterNewRelease === true ? null : true)}
              >
                新上架
              </Button>
              <Button
                variant={filterLandscape === true ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterLandscape(filterLandscape === true ? null : true)}
              >
                橫式
              </Button>
              <Button
                variant={sortMode === "ad_unlock_desc" ? "default" : "outline"}
                size="sm"
                onClick={() =>
                  setSortMode(sortMode === "ad_unlock_desc" ? "default" : "ad_unlock_desc")
                }
                title="依本月廣告解鎖次數從高到低排序"
              >
                本月廣告解鎖↓
              </Button>
            </div>
          </div>
        }
      />

      {/* Drama Grid */}
      {filteredDramas.length === 0 ? (
        dramas?.length === 0 ? (
          <AdminEmptyState
            icon={<Film className="h-5 w-5" />}
            title="尚無劇集資料"
            actionHref="/dramas/new"
            actionLabel="新增第一個劇集"
          />
        ) : (
          <AdminEmptyState
            icon={<Search className="h-5 w-5" />}
            title="沒有找到符合條件的劇集"
            description="請嘗試調整搜尋條件或篩選器"
          />
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {filteredDramas.map((drama) => {
            const id = drama.id || drama.drama_id;
            return (
              <DramaCard
                key={id}
                drama={drama}
                monthlyAdUnlockCount={
                  drama.ad_unlock_enabled === true ? totalsMap[id] ?? 0 : undefined
                }
                onClick={() => handleCardClick(id)}
              />
            );
          })}
        </div>
      )}
    </AdminPage>
  );
}
